import Immutable from 'immutable';
import TextFormatter from 'utils/TextFormatter';
import { textUtils } from './textUtils';
import {
    customerOwnedServiceCodes, sikMoveTransferCodes, eeroServiceCodes,
    internet1200Codes, bizInternet1200Codes, wholesale1200Codes, internet2000Codes,
    free1200Modems, fiberServiceCodes, fiberTerminalServiceCodes,
    WIFI_ACCESS_ROUTER as TPLink, wifiRangeExtenderServiceCodes, greenFieldSignalAccessCodes,
    lteFailoverCodes, legacyFiberServiceCodes, eeroUniversalCodes, eeroUniversalAdditionalCodes, yttvIntentServiceCodes, yttvSTBServiceCodes,
    eero7PrimServiceCodes, eero7AddtServiceCodes, eero7EmployeePrimServiceCodes, eero7EmployeeAddtServiceCodes,
    eeroCommunitySC
} from './accountEquipmentValidators';
import { isSrOrDi } from './serviceTypeUtils';

class EquipmentUtils {
    static isHsdEquipment(outlet, modemTypes = Immutable.fromJS([
        'hsd',
        'modem',
        'mg',
        'wireless modem',
        'emta modem',
        'ultra',
        'mg dcx'
    ])) {
        if (!outlet || !outlet.get('currentPortType')) {
            return false;
        }
        const hsdTypes = modemTypes.find(item => {
            if (item.toLowerCase() === outlet.get('currentPortType').trim().toLowerCase()) {
                return item;
            }
        });

        if (hsdTypes) {
            return true;
        }
        return false;

    }

    static getSqcableEquipment(equipmentDetails) {
        if (!equipmentDetails) {
            return new Immutable.List();
        }

        const outletsToSqc = equipmentDetails.filter((item) => {

            const dosqc = item
                .get('sqcCheckFlag');

            if (dosqc) {
                return true;
            }

            return false;

        });

        return outletsToSqc;
    }


    static getUltraList(list) {
        if (!list) {
            return null;
        }
        return list.filter((item) => {

            const matchingItem = item
                .get('requiredPortType')
                .find((port) => {
                    if (port === 'ULTRA') {
                        return port;
                    }
                });

            if (matchingItem) {
                return true;
            }

            return false;
        });
    }

    static isHitable(outlet) {
        if (outlet.get('make') === textUtils.equipment.eero.make) {
            return false;
        }

        if (outlet.get('action') === 'add') {
            return false;
        }

        if (outlet.get('action') === 'remove') {
            return false;
        }

        if (outlet.get('action') === 'update') {
            return false;
        }

        if (outlet.get('seriaNumber')) {
            return true;
        }

        return false;
    }

    static getEquipmentStatusDescription(status) {

        if (!status) {
            return null;
        }

        let description = status.toString();

        switch (status) {
            case 'B':
                description = 'broken, waiting repair';
                break;
            case 'D':
                description = 'bad serial number';
                break;
            case 'J':
                description = 'scrapped/destroyed';
                break;
            case 'L':
                description = 'reported as lost';
                break;
            case 'N':
                description = 'uncollected from cus - lost';
                break;
            case 'P':
                description = 'sold';
                break;
            case 'Q':
                description = 'new from vendor';
                break;
            case 'R':
                description = 'replaced by vendor';
                break;
            case 'S':
                description = 'stolen';
                break;
            case 'W':
                description = 'at factory for warranty';
                break;
            case '1':
                description = 'in inventory';
                break;
            case '2':
                description = 'at factory for repair';
                break;
            case '3':
                description = 'at factory to be replaced';
                break;
            case '4':
                description = 'untrackable assigned to wow empl';
                break;
            case '5':
                description = 'installed at customer';
                break;
            case '6':
                description = 'in invtory frm tech to be tested';
                break;
            case '7':
                description = 'assigned to tech/installer';
                break;
            case '8':
                description = 'waiting for repair';
                break;
            case 'T':
                description = 'returned to tech/installer';
                break;
            case 'V':
                description = 'write off';
                break;
            case 'O':
                description = 'obsolete';
                break;
            case 'C':
                description = 'conversion clean up';
                break;
            case 'U':
                description = 'untracked from csg conversion';
                break;
            case 'X':
                description = 'csg equip used in comx';
                break;

        }

        return TextFormatter.humanizeTitle(description);
    }

    static isCustomerOwnedModem(outlet) {
        if (outlet) {
            const serviceCodeList = outlet.get('serviceCodeList');

            if (serviceCodeList) {
                const hasCustOwnedModem = serviceCodeList.toJS().some(serviceCode => customerOwnedServiceCodes.includes(serviceCode.code.trim()));
                const custOwnedModemToBeRemoved = serviceCodeList.toJS().some(serviceCode => customerOwnedServiceCodes.includes(serviceCode.code.trim()) && serviceCode.status.trim() === "Remove");

                return hasCustOwnedModem && !custOwnedModemToBeRemoved;
            }
        }
        return false;
    }

    static isSkinnyInstallMoveTransfer(outletList) {
        if (outletList) {
            return outletList.toJS().some(outlet =>
                outlet.serviceCodeList.some(serviceCode =>
                    sikMoveTransferCodes.includes(serviceCode.code.trim())));
        }
        return false;
    }

    static isModemEquipmentNeeded(outlet) {
        if (outlet) {
            return outlet.get('requiredPortType').some(port => port === "HSD");
        }
        return false;
    }

    static isLTEEquipmentNeeded(outlet) {
        if (outlet) {
            return outlet.get('requiredPortType').some(port => port === "LTE");
        }
        return false;
    }

    static isVOIPOutlet(outlet) {
        if (outlet) {
            return outlet.get('requiredPortType').some(port => port === "VOIP");
        }
        return false;
    }

    static isModemInternet1200Type(outlet) {
        return (outlet.get("serviceCodeList").some(sc => internet1200Codes.includes(sc.get('code').trim())) &&
            outlet.get("serviceCodeList").some(sc => free1200Modems.includes(sc.get('code').trim()))) ||
            outlet.get("serviceCodeList").some(sc => bizInternet1200Codes.includes(sc.get('code').trim())) ||
            outlet.get("serviceCodeList").some(sc => wholesale1200Codes.includes(sc.get('code').trim()));
    }

    static isModemInternet2000Type(outlet) {
        if (outlet) {
            return outlet.get("serviceCodeList").some(sc => internet2000Codes.includes(sc.get('code').trim()));
        }
    }

    static hasWhwifiEquipment(outletList) {
        return outletList.toJS().some(outlet => outlet.serviceCodeList.some(sc => eeroServiceCodes.includes(sc.code.trim())));
    }

    static isToShowGponDropDown(outlet) {
        return outlet.get("serviceCodeList").some(sc => legacyFiberServiceCodes.includes(sc.get('code').trim()));
    }

    static isGponFiberModemType(outlet) {
        return outlet.get("serviceCodeList").some(sc => fiberServiceCodes.includes(sc.get('code').trim()));
    }

    static isFiberModemType(outlet) {
        const fiberSCs = fiberServiceCodes.concat(['6144']);
        return outlet.get("serviceCodeList").some(sc => fiberSCs.includes(sc.get('code').trim()));
    }

    static isFiberTerminal(outlet) {
        return outlet.get("serviceCodeList").some(sc => fiberTerminalServiceCodes.includes(sc.get('code').trim()));
    }

    static isLTEFailover(outlet) {
        return outlet.get("serviceCodeList").some(sc => lteFailoverCodes.includes(sc.get('code').trim()))
    }

    static isEEROUniversal(outlet) {
        return outlet.get("serviceCodeList").some(sc => eeroUniversalCodes.includes(sc.get('code').trim()))
    }

    static isWiFiRangeExtender(outlet) {
        return outlet.get("serviceCodeList").some(sc => wifiRangeExtenderServiceCodes.includes(sc.get('code').trim()))
    }

    static isToSearchByAllowedModels(outlet) {
        return (this.isModemInternet1200Type(outlet) && !this.is1200EquipmentDowngrade(outlet)) || this.isFiberTerminal(outlet) || this.isLTEFailover(outlet) ||
            (this.isModemInternet2000Type(outlet) && !this.is2000EquipmentDowngrade(outlet)) || this.isEEROUniversal(outlet)
    }

    static isGreenfieldAccount(signalAccessCode) {
        return greenFieldSignalAccessCodes.includes(signalAccessCode);
    }

    static isWHWFUpgrade(outletList) {
        if (outletList) {
            const whwfOutlets = outletList.filter(outlet => outlet.get('serviceCodeList').some(sc => eeroServiceCodes.includes(sc.get('code').trim() || TPLink === sc.get('code').trim())));
            const checkRemove = whwfOutlets.some(outlet => outlet.get('serviceCodeList').some(sc => sc.get('status').trim() === "Remove"));
            const checkAdd = whwfOutlets.some(outlet => outlet.get('serviceCodeList').some(sc => sc.get('status').trim() === "Add"));

            return checkRemove && checkAdd;
        }
        return false;
    }

    static isEquipmentDowngrade(sc) {
        return sc.get('status').trim() === 'Remove';
    }

    static is1200EquipmentDowngrade(outlet) {
        return outlet.get('serviceCodeList').some(sc => internet1200Codes.includes(sc.get('code').trim())
            && this.isEquipmentDowngrade(sc));
    }

    static is2000EquipmentDowngrade(outlet) {
        return outlet.get('serviceCodeList').some(sc => internet2000Codes.includes(sc.get('code').trim())
            && this.isEquipmentDowngrade(sc));
    }

    static isEquipmentRemovalOnly(outlet) {
        return !outlet.get('serviceCodeList').some(sc => sc.get('status').trim() === "Add");
    }

    static isOntActivation(outletList, signalAccessCode) {
        return outletList.some(outlet => this.isGponFiberModemType(outlet)) && this.isGreenfieldAccount(signalAccessCode);
    }

    static shouldShowONTMessage(outletList, signalAccessCode) {
        return outletList.some(outlet => this.isFiberModemType(outlet)) && this.isGreenfieldAccount(signalAccessCode);
    }

    static isEeroCommunity(outletList, orderType) {
        const hasSC = outletList.some(outlet =>
            outlet.get('serviceCodeList').some(sc =>
                eeroCommunitySC.includes(sc.get('code').trim())));
        const hasOT = !isSrOrDi(orderType);

        return hasSC && hasOT;
    }

    static calculateAditionalEquipmentMax(additionalEquipment, equipmentDetails) {
        const maxEquipment = 5,
            maxNonEquipment = 1,
            maxWifiExtenderPerAccount = 2,
            maxyttvSTBPerAccount = 6,
            maxEero7EmployeePerAccount = 5,
            maxEero7PrimPerAccount = 1;

        //Business logic for YT leased boxes
        if (
            yttvSTBServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const yttvSTBcount = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => yttvSTBServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxyttvSTBPerAccount - yttvSTBcount;
        }

        //Business logic for Wi-Fi Extenders
        if (
            wifiRangeExtenderServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const accWifiExtcount = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => wifiRangeExtenderServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxWifiExtenderPerAccount - accWifiExtcount;
        }

        //Business logic for EERO 7
        if (eero7AddtServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const eero7count = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => eero7AddtServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxEquipment - eero7count;
        }

        if (eero7PrimServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const eero7count = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => eero7PrimServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxEero7PrimPerAccount - eero7count;
        }

        if (eero7EmployeePrimServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const eero7count = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => eero7EmployeePrimServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxEero7PrimPerAccount - eero7count;
        }

        if (eero7EmployeeAddtServiceCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const eero7count = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => eero7EmployeeAddtServiceCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxEero7EmployeePerAccount - eero7count;
        }

        if (eeroUniversalAdditionalCodes.includes(additionalEquipment.get("code").trim()) &&
            equipmentDetails
        ) {
            const eeroUniversalCount = equipmentDetails
                .filter((outlet) =>
                    outlet
                        .get("serviceCodeList")
                        .some((sc) => eeroUniversalAdditionalCodes.includes(sc.get("code").trim()))
                )
                .count();

            return maxEquipment - eeroUniversalCount;
        }

        if (yttvIntentServiceCodes.includes(additionalEquipment.get("code").trim())) {
            return maxNonEquipment;
        }

        return additionalEquipment.get("isEquipment")
            ? maxEquipment
            : maxNonEquipment;
    }

    static getDongleEquipmentSerialNumber(serialNumber) {
        switch (serialNumber.length) {
            case 16:
                return '00' + serialNumber;
            case 17:
                return '0' + serialNumber;
            default:
                return serialNumber;
        }
    }
}

export default EquipmentUtils;



// WEBPACK FOOTER //
// ./src/utils/EquipmentUtils.js